import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { anyService } from "@/services/breeze/AnyService";
import {
  DeviceSourceConfig,
  tenantPushService,
} from "@/services/breeze/TenantPushService";
import { userService } from "@/services/breeze/UserService";
import { UserWithRole } from "@/types/breeze/User";

export interface UserWithDevices {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  display_name: string;
  device_count: number;
  by_provider: Record<string, number>;
}

export function useUsersWithDevices() {
  const { data: registry, isLoading: registryLoading } = useQuery({
    queryKey: ["push-devices", "for-users-list"],
    queryFn: () => tenantPushService.getDevices(false),
  });
  const { data: credentials, isLoading: credsLoading } = useQuery({
    queryKey: ["push-credentials"],
    queryFn: () => tenantPushService.getCredentials(),
  });
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ["tenant-users", "for-push"],
    queryFn: () => userService.getAllUsers(),
  });

  const entityFieldSources = useMemo(
    () =>
      (credentials?.providers ?? [])
        .map((p) => {
          const ds = p.metadata?.device_source as DeviceSourceConfig | undefined;
          if (!ds || ds.kind !== "entity_field" || !ds.entity || !ds.token_field) return null;
          if (!ds.owner_user_field) return null;
          return { provider: p.provider, source: ds };
        })
        .filter(Boolean) as Array<{
          provider: "fcm" | "web_push" | "apns";
          source: DeviceSourceConfig & { entity: string; token_field: string; owner_user_field: string };
        }>,
    [credentials],
  );

  const entityFieldQueries = useQueries({
    queries: entityFieldSources.map(({ provider, source }) => ({
      queryKey: ["push-devices", "entity-field-users", provider, source.entity],
      queryFn: async () => {
        const fields = [source.token_field, source.owner_user_field].filter(Boolean) as string[];
        const result = await anyService.getAll<Record<string, unknown>>(source.entity, fields, "", 1, 500);
        return { provider, source, items: result.items };
      },
    })),
  });

  const userById = useMemo<Record<number, UserWithRole>>(() => {
    const map: Record<number, UserWithRole> = {};
    (users ?? []).forEach((u) => (map[u.id] = u));
    return map;
  }, [users]);

  const usersWithDevices = useMemo<UserWithDevices[]>(() => {
    const counts = new Map<number, { total: number; byProvider: Record<string, number> }>();
    const bump = (uid: number, provider: string) => {
      const entry = counts.get(uid) ?? { total: 0, byProvider: {} };
      entry.total += 1;
      entry.byProvider[provider] = (entry.byProvider[provider] ?? 0) + 1;
      counts.set(uid, entry);
    };

    (registry?.devices ?? []).forEach((d) => {
      const uid = d.user_id ?? (d.owner_entity_name === "anythink_users" ? d.owner_entity_id : null);
      if (uid != null) bump(uid, d.provider);
    });

    entityFieldQueries.forEach((q) => {
      if (!q.data) return;
      const { provider, source, items } = q.data;
      items.forEach((row) => {
        const token = row[source.token_field] as string | null | undefined;
        if (!token) return;
        const ownerRaw = row[source.owner_user_field];
        const uid =
          typeof ownerRaw === "object" && ownerRaw !== null && "id" in ownerRaw
            ? (ownerRaw as { id: number }).id
            : typeof ownerRaw === "number"
              ? ownerRaw
              : null;
        if (uid != null) bump(uid, provider);
      });
    });

    const result: UserWithDevices[] = [];
    counts.forEach(({ total, byProvider }, uid) => {
      const u = userById[uid];
      if (!u) return; // user no longer exists / not visible to this admin
      const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
      const display = fullName ? `${fullName} — ${u.email}` : u.email;
      result.push({
        id: u.id,
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
        display_name: display,
        device_count: total,
        by_provider: byProvider,
      });
    });
    return result.sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [
    registry,
    userById,
    entityFieldQueries.map((q) => q.dataUpdatedAt).join(","),
  ]);

  return {
    users: usersWithDevices,
    userById,
    isLoading: registryLoading || credsLoading || usersLoading || entityFieldQueries.some((q) => q.isLoading),
  };
}
